import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../api/db';
import ArticleCard from '../components/common/ArticleCard';
import ShareQR from '../components/common/ShareQR';
import { useSettings } from '../context/SettingsContext';

const TopTile = ({ title, img, link }) => (
  <a 
    href={link} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="block group rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100 h-64 relative"
  >
    <img 
      src={img} 
      alt={title} 
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none"></div>
    <div className="absolute bottom-0 left-0 w-full p-6 text-white">
      <h3 className="font-bold text-2xl tracking-wide leading-tight">{title}</h3>
    </div>
  </a>
);

const Home = () => {
  const { settings } = useSettings();
  const [latestArticles, setLatestArticles] = useState([]);
  const [sliderArticles, setSliderArticles] = useState([]);
  const [sidebarArticles, setSidebarArticles] = useState([]);

  useEffect(() => {
    const load = async () => {
      const articles = await db.getArticles(true);
      setLatestArticles(articles.slice(0, 5));
      const byViews = [...articles].sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
      setSliderArticles(byViews.slice(0, 3));
      setSidebarArticles(articles.slice(0, 10));
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* 1. Top Section: 3 Tiles + Picture + Sidebar List */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 items-start">
          
          {/* Row 1: The 3 Tiles */}
          <TopTile title={settings.tile_1_label} img={settings.tile_1_img} link={settings.tile_1_url} />
          <TopTile title={settings.tile_2_label} img={settings.tile_2_img} link={settings.tile_2_url} />
          <TopTile title={settings.tile_3_label} img={settings.tile_3_img} link={settings.tile_3_url} />

          {/* Row 2: Image spanning 2 cols, Latest Posts in 3rd col */}
          <div className="md:col-span-2 flex items-center justify-center py-4">
            <img 
              src={settings.home_logo_url}
              alt={settings.app_name}
              className="max-w-full h-auto object-contain"
            />
          </div>
          
          <div className="md:col-span-1">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 leading-tight mb-4 pb-2 border-b border-gray-200">
                Latest Posts
              </h2>
              <ul className="space-y-2.5">
                {sidebarArticles.map((article, i) => (
                  <li key={article.id} className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 font-medium mt-0.5 w-4 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <Link 
                      to={`/blog/${article.slug}`} 
                      className="text-gray-700 font-medium text-sm transition-colors line-clamp-2 leading-snug"
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-primary)'}
                      onMouseLeave={e => e.currentTarget.style.color = ''}
                    >
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 2. Purpose Text + QR */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">
                {settings.home_hero_title}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {settings.home_hero_body}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <ShareQR
                url={typeof window !== 'undefined' ? window.location.href.split('#')[0] : '/'}
                title={settings.home_hero_title || 'Haringey Learns'}
                size="lg"
              />
              <span className="text-xs font-medium text-gray-400 tracking-wide select-none">
                Scan the QR code
              </span>
            </div>
          </div>
        </div>

        {/* 3. Latest Articles List */}
        <div className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b border-gray-100 pb-4">
            Latest Articles
          </h2>
          <div className="flex flex-col gap-8">
            {latestArticles.map(article => (
              <Link 
                key={article.id} 
                to={`/blog/${article.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col sm:flex-row gap-8 group hover:bg-gray-50 p-4 -mx-4 rounded-2xl transition-colors"
                onMouseEnter={e => { const h = e.currentTarget.querySelector('.article-title'); if(h) h.style.color = 'var(--brand-primary)'; }}
                onMouseLeave={e => { const h = e.currentTarget.querySelector('.article-title'); if(h) h.style.color = ''; }}
              >
                <div className="w-full sm:w-2/5 flex-shrink-0 relative">
                  <img 
                    src={article.cover_image?.trim() || '/fallback.png'} 
                    alt={article.title} 
                    onError={e => { e.currentTarget.src = '/fallback.png'; }}
                    className="w-full h-56 object-cover rounded-xl shadow-sm group-hover:shadow-md transition-shadow"
                  />
                  {article.category?.name && (
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-xs font-semibold px-3 py-1 rounded-full" style={{ color: 'var(--brand-primary)' }}>
                      {article.category.name}
                    </span>
                  )}
                </div>
                <div className="w-full sm:w-3/5 flex flex-col justify-center">
                  <h3 className="article-title text-2xl font-bold text-gray-900 mb-4 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-3 text-lg">
                    {article.summary}
                  </p>
                  {article.author_name && (
                    <p className="mt-3 text-sm text-gray-500">By <span className="font-medium text-gray-700">{article.author_name}</span></p>
                  )}
                  <span className="mt-3 text-sm font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-transform" style={{ color: 'var(--brand-primary)' }}>
                    Read more &rarr;
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* 4. More from the blogs — 2×3 grid */}
        {sliderArticles.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b border-gray-100 pb-4">
              Most Popular
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sliderArticles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Home;